import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Calendar, User, ArrowLeft, Tag, Share2, ExternalLink, Newspaper } from "lucide-react";

import { SidebarAds } from "./BoxAd";
import { InArticleAd } from "./AdUnit";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export const NewsArticlePage = () => {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [relatedArticles, setRelatedArticles] = useState([]);

  useEffect(() => {
    fetchArticle();
  }, [slug]);

  const fetchArticle = async () => {
    try {
      const res = await axios.get(`${API}/news/articles/${slug}`);
      setArticle(res.data);

      // Fetch related articles from same category
      if (res.data.category) {
        const relatedRes = await axios.get(`${API}/news/articles?category=${res.data.category}&limit=4`);
        setRelatedArticles(
          relatedRes.data.articles.filter((a) => a.slug !== slug).slice(0, 3)
        );
      }
    } catch (err) {
      console.error("Error fetching article:", err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", {
      weekday: "long",
      month: "long",
      day: "numeric",
      year: "numeric"
    });
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: article.title,
        url: window.location.href
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
      alert("Link copied to clipboard!");
    }
  };

  if (loading) {
    return (
      <main className="min-h-screen bg-[#050505]">
        
        
        <div className="pt-8 flex items-center justify-center min-h-[60vh]">
          <div className="w-8 h-8 border-2 border-white/20 border-t-white rounded-full animate-spin"></div>
        </div>
      </main>
    );
  }

  if (!article) {
    return (
      <main className="min-h-screen bg-[#050505]">
        
        
        <div className="pt-8 flex flex-col items-center justify-center min-h-[60vh]">
          <Newspaper className="w-16 h-16 text-zinc-700 mb-4" />
          <h1 className="font-heading text-2xl text-white mb-2">Article Not Found</h1>
          <p className="text-zinc-500 mb-6">This article may have been removed or is unavailable.</p>
          <Link
            to="/news"
            className="flex items-center gap-2 text-white hover:text-zinc-300"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to News
          </Link>
        </div>
        
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-[#050505]" data-testid="news-article-page">
      
      

      {/* Article Header */}
      <section className="pt-8 pb-8 bg-gradient-to-b from-zinc-900 to-[#050505]">
        <div className="max-w-4xl mx-auto px-4 md:px-8">
          {/* Back Link */}
          <Link
            to="/news"
            className="inline-flex items-center gap-2 text-zinc-500 hover:text-white mb-6 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm">Back to News</span>
          </Link>

          {/* Category & Date */}
          <div className="flex items-center gap-3 mb-4">
            {article.category && (
              <span className="px-3 py-1 bg-white text-black text-xs font-bold uppercase">
                {article.category}
              </span>
            )}
            <span className="text-zinc-500 text-sm">
              {formatDate(article.published_at)}
            </span>
          </div>

          {/* Title */}
          <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-black uppercase tracking-tight text-white mb-6">
            {article.title}
          </h1>

          {/* Author & Meta */}
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-4">
              {/* Author */}
              <div className="flex items-center gap-3">
                {article.author_headshot ? (
                  <img
                    src={article.author_headshot}
                    alt={article.author_name}
                    className="w-10 h-10 rounded-full object-cover"
                  />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-zinc-800 flex items-center justify-center">
                    <User className="w-5 h-5 text-zinc-500" />
                  </div>
                )}
                <div>
                  <p className="text-white font-medium">{article.author_name}</p>
                  {/* Hide author_type for RSS articles */}
                  {article.author_type && article.author_type !== 'rss' && (
                    <p className="text-zinc-500 text-xs capitalize">{article.author_type}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-4">
              <button
                onClick={handleShare}
                className="flex items-center gap-2 px-3 py-2 bg-zinc-800 text-zinc-400 hover:text-white text-sm transition-colors"
              >
                <Share2 className="w-4 h-4" />
                Share
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Image */}
      {article.featured_image && (
        <div className="max-w-5xl mx-auto px-4 md:px-8 mb-8">
          <div className="aspect-video bg-zinc-900 overflow-hidden">
            <img
              src={article.featured_image}
              alt={article.title}
              className="w-full h-full object-cover"
            />
          </div>
        </div>
      )}

      {/* Article Content with Sidebar */}
      <div className="max-w-[1400px] mx-auto px-4 md:px-8 pb-16">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Main Content */}
          <article className="flex-1 max-w-4xl">
            <div
              className="prose prose-invert prose-lg max-w-none
                prose-headings:font-heading prose-headings:uppercase prose-headings:tracking-tight
                prose-p:text-zinc-300 prose-p:leading-relaxed
                prose-a:text-white prose-a:underline hover:prose-a:text-zinc-300
                prose-strong:text-white
                prose-blockquote:border-l-white prose-blockquote:text-zinc-400
                prose-img:rounded-none prose-img:border prose-img:border-white/10"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            {/* Google AdSense In-Article Ad */}
            <InArticleAd slot="YOUR_AD_SLOT_ID" className="my-8" />

            {/* Tags */}
            {article.tags && article.tags.length > 0 && (
              <div className="flex items-center gap-2 mt-8 pt-8 border-t border-white/10">
                <Tag className="w-4 h-4 text-zinc-500" />
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 bg-zinc-800 text-zinc-400 text-xs"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            )}

            {/* Source Link (for RSS articles) */}
            {article.source_url && (
              <div className="mt-6 p-4 bg-zinc-900/50 border border-white/10">
                <p className="text-zinc-500 text-sm mb-2">Originally published at:</p>
                <a
                  href={article.source_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-white hover:text-zinc-300"
                >
                  <ExternalLink className="w-4 h-4" />
                  {article.source_url}
                </a>
              </div>
            )}
          </article>

          {/* Sidebar with Box Ads */}
          <aside className="hidden lg:block w-[300px] shrink-0">
            <div className="sticky top-32">
              <SidebarAds count={2} placement="article_side" />
            </div>
          </aside>
        </div>
      </div>

      {/* Related Articles */}
      {relatedArticles.length > 0 && (
        <section className="bg-zinc-900/30 border-t border-white/5 py-12">
          <div className="max-w-[1400px] mx-auto px-4 md:px-8">
            <h2 className="font-heading text-2xl font-bold uppercase text-white mb-6">
              Related Articles
            </h2>
            <div className="grid md:grid-cols-3 gap-6">
              {relatedArticles.map((related) => (
                <Link
                  key={related.id}
                  to={`/news/${related.slug}`}
                  className="group bg-zinc-900/50 border border-white/5 hover:border-white/20 transition-colors"
                >
                  <div className="aspect-video bg-zinc-800 overflow-hidden">
                    {related.featured_image ? (
                      <img
                        src={related.featured_image}
                        alt={related.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Newspaper className="w-10 h-10 text-zinc-700" />
                      </div>
                    )}
                  </div>
                  <div className="p-4">
                    <h3 className="font-heading text-lg font-bold uppercase text-white line-clamp-2 group-hover:text-zinc-300 transition-colors">
                      {related.title}
                    </h3>
                    <p className="text-zinc-500 text-sm mt-2">
                      {formatDate(related.published_at)}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      
    </main>
  );
};

export default NewsArticlePage;
